import { z } from "zod";
import { BRANDING_TOKEN_MAP } from "../branding/tokens.js";
/**
 * Branding overrides are validated against the token registry, not against a
 * hand-written schema — so adding a token to `tokens.ts` automatically makes it
 * settable and validated, with no second place to update.
 *
 * `null` is always allowed: it is how an org resets a field back to the
 * platform default.
 */
const HEX = /^#[0-9a-fA-F]{6}$/;
const SIZE = /^\d{1,3}(?:\.\d+)?(?:px|rem)$/;
function validateTokenValue(def, value, ctx, path) {
    const fail = (message) => {
        ctx.addIssue({ code: "custom", message, path: [path] });
    };
    switch (def.type) {
        case "color":
            if (!HEX.test(value))
                fail("Use a 6-digit hex color, like #C54E1B");
            break;
        case "size":
            if (!SIZE.test(value))
                fail("Use a size like 10px or 0.5rem");
            break;
        case "font":
        case "enum":
            if (def.options && !def.options.includes(value)) {
                fail(`Choose one of: ${def.options.join(", ")}`);
            }
            break;
        case "url":
        case "link":
            // Only our own storage or an https URL. No javascript:, no data:.
            if (!/^https:\/\/[^\s"'<>]+$/i.test(value) && !/^\/[^\s"'<>]*$/.test(value)) {
                fail("Must be an https:// address");
            }
            break;
        case "email":
            if (!z.email().safeParse(value).success)
                fail("Enter a valid email address");
            break;
        case "phone":
            if (!/^\+?[\d\s\-()]{6,20}$/.test(value))
                fail("Enter a valid phone number");
            break;
        case "text":
            if (value.length > 200)
                fail("At most 200 characters");
            // Reject anything that could be interpreted as markup.
            if (/[<>]/.test(value))
                fail("Angle brackets are not allowed");
            break;
    }
}
export const brandingOverridesSchema = z
    .record(z.string(), z.union([z.string(), z.null()]))
    .superRefine((input, ctx) => {
    for (const [key, value] of Object.entries(input)) {
        const def = BRANDING_TOKEN_MAP[key];
        if (!def) {
            ctx.addIssue({ code: "custom", message: `Unknown branding setting: ${key}`, path: [key] });
            continue;
        }
        if (value === null || value === "")
            continue; // reset to inherited
        validateTokenValue(def, value, ctx, key);
    }
})
    .transform((input) => {
    const out = {};
    for (const [key, value] of Object.entries(input)) {
        out[key] = value === "" ? null : value;
    }
    return out;
});
export const updateBrandingSchema = z.object({
    branding: brandingOverridesSchema,
    /**
     * Set true only when the caller has seen and accepted the contrast warnings.
     * The API still refuses errors that make body text unreadable.
     */
    acknowledgeWarnings: z.boolean().optional(),
});
export const applyPresetSchema = z.object({
    presetId: z.string().min(1),
});
export const resetBrandingSchema = z.object({
    /** Omit to reset the whole theme; pass keys to reset only those fields. */
    keys: z.array(z.string()).optional(),
});
//# sourceMappingURL=branding.js.map